import { FC, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { PageTitle } from '../../../_metronic/layout/core';
import {
  ShopList,
  ModalShopForm,
  ShopModel,
  listShops,
  createShop,
  updateShop,
  deleteShop,
} from '../../modules/shops';
import {
  DistributorModel,
  PointOfSaleModel,
  listDistributors,
  listPointsOfSale,
  createPointOfSale,
  updatePointOfSale,
  deletePointOfSale,
} from '../../modules/distributors';
import { toast } from 'react-toastify';

const ShopsPage: FC = () => {
  const intl = useIntl();
  const [shops, setShops] = useState([]);
  const [distributors, setDistributors] = useState<Array<DistributorModel>>([]);
  // Shop id -> its distributor relation. A shop has at most one.
  const [pointsOfSaleByShopId, setPointsOfSaleByShopId] = useState<
    Record<number, PointOfSaleModel>
  >({});
  const [loading, setLoading] = useState(false);
  const [modalFormVisible, setModalFormVisible] = useState(false);
  const [editingShop, setEditingShop] = useState<ShopModel | undefined>(
    undefined
  );

  const distributorNamesByShopId = Object.fromEntries(
    Object.entries(pointsOfSaleByShopId).map(([shopId, pointOfSale]) => [
      shopId,
      pointOfSale.distributor_name,
    ])
  );

  const fetchShops = async () => {
    try {
      setLoading(true);
      const [{ data: shopsData }, { data: pointsOfSaleData }] =
        await Promise.all([listShops(), listPointsOfSale()]);
      setShops(shopsData);
      setPointsOfSaleByShopId(
        Object.fromEntries(
          pointsOfSaleData.map((pointOfSale) => [pointOfSale.shop, pointOfSale])
        )
      );
    } catch (error) {
      console.warn(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDistributors = async () => {
    try {
      const { data } = await listDistributors('active');
      setDistributors(data);
    } catch (error) {
      console.warn(error);
    }
  };

  useEffect(() => {
    fetchShops();
    fetchDistributors();
  }, []);

  function onAddShop() {
    setEditingShop(undefined);
    setModalFormVisible(true);
  }

  /**
   * The shop itself lives behind the public /api/shops/ endpoint while its
   * distributor link lives behind the staff-only B2B one, so saving the form
   * means reconciling the relation separately.
   */
  async function syncPointOfSale(shopId: number, distributorId: number | null) {
    const pointOfSale = pointsOfSaleByShopId[shopId];

    if (!distributorId) {
      if (pointOfSale) {
        await deletePointOfSale(pointOfSale.id);
      }
      return;
    }

    if (!pointOfSale) {
      await createPointOfSale({ distributor: distributorId, shop: shopId });
    } else if (pointOfSale.distributor !== distributorId) {
      await updatePointOfSale({
        id: pointOfSale.id,
        distributor: distributorId,
        shop: shopId,
      });
    }
  }

  async function onSaveShop(shop: ShopModel, distributorId: number | null) {
    setModalFormVisible(false);
    setLoading(true);
    try {
      const response = shop.id
        ? await updateShop(shop)
        : await createShop(shop);
      await syncPointOfSale(response.data.id, distributorId);
      fetchShops();
      toast.success(intl.formatMessage({ id: 'shops.saved' }));
    } catch (error) {
      console.warn(error);
      const detail = error?.response?.data?.shop?.[0];
      toast.error(detail || intl.formatMessage({ id: 'shops.error' }));
      fetchShops();
    } finally {
      setLoading(false);
    }
  }

  async function onRemoveShop(shop: ShopModel) {
    const hasConfirmed = window.confirm(
      intl.formatMessage({ id: 'shops.confirmRemove' })
    );
    if (!hasConfirmed) {
      return;
    }

    setLoading(true);
    try {
      await deleteShop(shop.id);
      fetchShops();
      toast.success(intl.formatMessage({ id: 'shops.removed' }));
    } catch (error) {
      console.warn(error);
    } finally {
      setLoading(false);
    }
  }

  function onEditShop(shop: ShopModel) {
    setEditingShop(shop);
    setModalFormVisible(true);
  }

  return (
    <>
      <PageTitle breadcrumbs={[]}>
        {intl.formatMessage({ id: 'menu.shops' })}
      </PageTitle>
      <div className="row">
        <div className="col-12">
          <div className="card p-10">
            <ShopList
              shops={shops}
              distributorNamesByShopId={distributorNamesByShopId}
              loading={loading}
              onAddShop={onAddShop}
              onEditShop={onEditShop}
              onRemoveShop={onRemoveShop}
            />
            <ModalShopForm
              shop={editingShop}
              distributors={distributors}
              distributorId={
                pointsOfSaleByShopId[editingShop?.id]?.distributor ?? null
              }
              visible={modalFormVisible}
              onCancel={() => setModalFormVisible(false)}
              onSaveShop={onSaveShop}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export { ShopsPage };
